import React, { useState, useEffect } from 'react';
import {
  FileText,
  Plus,
  Search,
  Building2,
  Printer,
  CheckCircle2,
  Calendar,
  Layers,
  ArrowRight,
  MessageSquare,
} from 'lucide-react';
import { QuotationItem } from '../types/index.ts';
import { WhatsAppShareModal, WhatsAppShareData } from '../components/common/WhatsAppShareModal.tsx';

interface QuotationsPageProps {
  onNavigate: (route: string, param?: string) => void;
}

export const QuotationsPage: React.FC<QuotationsPageProps> = ({ onNavigate }) => {
  const [quotations, setQuotations] = useState<QuotationItem[]>([]);
  const [boqs, setBoqs] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuotation, setSelectedQuotation] = useState<QuotationItem | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);

  // WhatsApp Sharing State
  const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
  const [shareData, setShareData] = useState<WhatsAppShareData | null>(null);

  const [form, setForm] = useState({
    projectId: '',
    boqId: '',
    validityDays: 30,
    discount: '0',
    terms: '1. 10% Mobilization advance upon PO release.\n2. Running invoices on bi-weekly milestone sign-off.\n3. 5% Retention money released after 90 days DLP.\n4. Standard 12-month quality guarantee on civil and electrical workmanship.',
  });

  const fetchQuotations = async () => {
    try {
      setLoading(true);
      const [qRes, bRes, pRes] = await Promise.all([
        fetch('/api/quotations'),
        fetch('/api/boqs'),
        fetch('/api/projects'),
      ]);
      const qData = await qRes.json();
      const bData = await bRes.json();
      const pData = await pRes.json();
      if (Array.isArray(qData)) setQuotations(qData);
      if (Array.isArray(bData)) {
        setBoqs(bData);
        if (bData.length > 0) {
          setForm((prev) => ({
            ...prev,
            boqId: String(bData[0].id),
            projectId: String(bData[0].projectId),
          }));
        }
      }
      if (Array.isArray(pData)) setProjects(pData);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuotations();
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await fetch('/api/quotations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setShowAddModal(false);
        fetchQuotations();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const formatPKR = (val: string | number) => {
    return 'Rs. ' + Math.round(Number(val || 0)).toLocaleString('en-PK');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
              Quotations &amp; Client Proposals
            </h1>
            <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-blue-100 text-blue-900">
              UBL Submissions
            </span>
          </div>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
            Auto-generated from approved BOQ estimations with standard commercial terms &amp; tax schedule.
          </p>
        </div>

        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-1.5 px-3.5 py-2 bg-[#0f2942] hover:bg-[#183d5f] text-white rounded-lg text-xs font-semibold shadow-2xs transition-colors self-start sm:self-auto"
        >
          <Plus className="w-4 h-4 text-amber-400" />
          <span>New Quotation</span>
        </button>
      </div>

      {/* Quotations List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {(Array.isArray(quotations) ? quotations : []).map((q) => (
          <div key={q.id} className="bg-white rounded-xl border border-slate-200 shadow-2xs p-5 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-blue-900">{q.quotationNumber}</span>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">
                {q.status.replace(/_/g, ' ')}
              </span>
            </div>

            <div>
              <div className="font-bold text-sm text-slate-900">{q.projectName}</div>
              <div className="text-xs text-slate-500 mt-0.5">Date: {q.date} • Validity: {q.validityDays} Days</div>
            </div>

            <div className="bg-slate-50 p-3 rounded-lg border border-slate-100 space-y-1 text-xs">
              <div className="flex justify-between text-slate-600">
                <span>Subtotal:</span>
                <span>{formatPKR(q.subtotal)}</span>
              </div>
              <div className="flex justify-between text-slate-600">
                <span>Sales Tax ({q.taxPercent}%):</span>
                <span>{formatPKR(q.taxAmount)}</span>
              </div>
              <div className="flex justify-between font-bold text-slate-900 pt-1 border-t border-slate-200">
                <span>Grand Total:</span>
                <span className="text-blue-900">{formatPKR(q.grandTotal)}</span>
              </div>
            </div>

            <div className="flex items-center justify-between pt-1">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setSelectedQuotation(q);
                    setShowPrintModal(true);
                  }}
                  className="text-xs font-semibold text-slate-700 hover:text-slate-900 flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded-lg transition-colors"
                >
                  <Printer className="w-3.5 h-3.5" />
                  <span>Proposal</span>
                </button>

                <button
                  onClick={() => {
                    setShareData({
                      title: `Official Quotation: ${q.quotationNumber}`,
                      docType: 'QUOTATION',
                      referenceNumber: q.quotationNumber,
                      projectName: q.projectName,
                      branchName: 'UBL Branch Works',
                      amount: formatPKR(q.grandTotal),
                      date: q.date,
                      detailsSummary: `Subtotal: ${formatPKR(q.subtotal)} + ${q.taxPercent}% PST (${formatPKR(q.taxAmount)}). Valid for ${q.validityDays} days.`,
                    });
                    setWhatsAppModalOpen(true);
                  }}
                  className="text-xs font-bold text-[#075e54] hover:text-white flex items-center gap-1.5 bg-[#25D366]/15 hover:bg-[#25D366] px-2.5 py-1.5 rounded-lg transition-colors"
                  title="Share quotation via WhatsApp"
                >
                  <MessageSquare className="w-3.5 h-3.5 text-[#25D366] group-hover:text-white" />
                  <span>WhatsApp</span>
                </button>
              </div>

              <button
                onClick={() => onNavigate('projects')}
                className="text-xs font-semibold text-blue-700 hover:text-blue-900 flex items-center gap-1"
              >
                <span>Project</span>
                <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Print / Formal Proposal View Modal */}
      {showPrintModal && selectedQuotation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-200 max-w-2xl w-full p-8 space-y-6 animate-in fade-in zoom-in-95 max-h-[90vh] overflow-y-auto font-sans">
            {/* Header / Letterhead */}
            <div className="flex items-start justify-between border-b-2 border-slate-900 pb-4">
              <div>
                <h2 className="text-xl font-black text-slate-900 tracking-tight">NAEEM BUILDER</h2>
                <p className="text-xs text-slate-600 font-medium">Construction, Renovation &amp; Project Engineering</p>
                <p className="text-[11px] text-slate-500">Suite 402, Trade Center, Karachi • NTN: 4182901-7</p>
              </div>
              <div className="text-right">
                <div className="text-base font-bold text-blue-900">COMMERCIAL QUOTATION</div>
                <div className="text-xs font-mono font-bold text-slate-700 mt-1">{selectedQuotation.quotationNumber}</div>
                <div className="text-xs text-slate-500">Date: {selectedQuotation.date}</div>
              </div>
            </div>

            {/* Recipient */}
            <div className="bg-slate-50 p-3 rounded-lg border border-slate-200 text-xs space-y-0.5">
              <div className="text-slate-500 font-semibold uppercase text-[10px]">Client Details:</div>
              <div className="font-bold text-slate-900">United Bank Limited (UBL) - Corporate Premises Division</div>
              <div className="text-slate-700">Project: {selectedQuotation.projectName}</div>
            </div>

            {/* Pricing Summary */}
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <table className="w-full text-xs text-left">
                <thead className="bg-slate-100 text-slate-700 uppercase tracking-wider text-[10px]">
                  <tr>
                    <th className="p-3">Scope Description</th>
                    <th className="p-3 text-right">Amount (PKR)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  <tr>
                    <td className="p-3">Full Civil, Tile Flooring, False Ceiling &amp; Painting Works</td>
                    <td className="p-3 text-right font-medium">{formatPKR(selectedQuotation.subtotal)}</td>
                  </tr>
                  <tr>
                    <td className="p-3">Provincial Sales Tax ({selectedQuotation.taxPercent}%)</td>
                    <td className="p-3 text-right font-medium">{formatPKR(selectedQuotation.taxAmount)}</td>
                  </tr>
                  <tr className="bg-slate-50 font-bold text-slate-900 text-sm">
                    <td className="p-3">Total Payable (Net of Tax)</td>
                    <td className="p-3 text-right text-blue-900">{formatPKR(selectedQuotation.grandTotal)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Commercial Terms */}
            <div className="space-y-1 text-xs text-slate-700">
              <div className="font-bold uppercase text-[10px] text-slate-500">Terms &amp; Conditions:</div>
              <div className="whitespace-pre-line leading-relaxed bg-slate-50 p-3 rounded-lg border border-slate-100 font-mono text-[11px]">
                {selectedQuotation.terms}
              </div>
            </div>

            {/* Signatures */}
            <div className="grid grid-cols-2 gap-8 pt-8 border-t border-slate-200 text-xs text-center">
              <div>
                <div className="font-bold text-slate-900">Naeem Akhtar</div>
                <div className="text-[11px] text-slate-500">Managing Director • Naeem Builder</div>
              </div>
              <div>
                <div className="font-bold text-slate-900">UBL Property Officer</div>
                <div className="text-[11px] text-slate-500">Corporate Real Estate Division</div>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 pt-4 border-t border-slate-200">
              <button
                type="button"
                onClick={() => {
                  setShareData({
                    title: `Official Quotation - ${selectedQuotation.quotationNumber}`,
                    docType: 'QUOTATION',
                    referenceNumber: selectedQuotation.quotationNumber,
                    projectName: selectedQuotation.projectName,
                    branchName: 'UBL Branch Premises',
                    amount: formatPKR(selectedQuotation.grandTotal),
                    date: selectedQuotation.date,
                    detailsSummary: `Full civil & electrical scope. Net: ${formatPKR(selectedQuotation.grandTotal)}. Terms: 10% Mobilization, running bills, 5% retention.`,
                  });
                  setWhatsAppModalOpen(true);
                }}
                className="px-4 py-2 bg-[#25D366] hover:bg-[#20bd5a] text-white rounded-lg text-xs font-bold flex items-center gap-1.5 shadow-2xs transition-colors"
              >
                <MessageSquare className="w-4 h-4 fill-current" />
                <span>Share via WhatsApp</span>
              </button>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowPrintModal(false)}
                  className="px-4 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 text-xs font-semibold"
                >
                  Close
                </button>
                <button
                  onClick={() => window.print()}
                  className="px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-lg text-xs font-semibold flex items-center gap-1.5"
                >
                  <Printer className="w-4 h-4" />
                  <span>Print Document</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Quotation Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-200 max-w-lg w-full p-5 space-y-4 animate-in fade-in zoom-in-95 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="font-bold text-slate-900 text-base">Generate Quotation from BOQ</h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-slate-400 hover:text-slate-600 text-sm font-semibold"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreate} className="space-y-3 text-xs">
              <div>
                <label className="block font-semibold text-slate-700 mb-1">Source BOQ Revision *</label>
                <select
                  required
                  className="w-full border border-slate-200 rounded-lg p-2"
                  value={form.boqId}
                  onChange={(e) => {
                    const b = boqs.find((item) => item.id === Number(e.target.value));
                    setForm({
                      ...form,
                      boqId: e.target.value,
                      projectId: b ? String(b.projectId) : form.projectId,
                    });
                  }}
                >
                  {(Array.isArray(boqs) ? boqs : []).map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.boqNumber} - {b.title} ({formatPKR(b.grandTotal)})
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Proposal Validity (Days)</label>
                  <input
                    type="number"
                    className="w-full border border-slate-200 rounded-lg p-2"
                    value={form.validityDays}
                    onChange={(e) => setForm({ ...form, validityDays: Number(e.target.value) })}
                  />
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Special Discount (Rs.)</label>
                  <input
                    type="number"
                    className="w-full border border-slate-200 rounded-lg p-2"
                    value={form.discount}
                    onChange={(e) => setForm({ ...form, discount: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Commercial Terms &amp; Warranties</label>
                <textarea
                  rows={4}
                  className="w-full border border-slate-200 rounded-lg p-2 font-mono text-[11px]"
                  value={form.terms}
                  onChange={(e) => setForm({ ...form, terms: e.target.value })}
                />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#0f2942] hover:bg-[#183d5f] text-white rounded-lg font-semibold"
                >
                  Generate Quotation
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* WhatsApp Share Modal */}
      <WhatsAppShareModal
        isOpen={whatsAppModalOpen}
        onClose={() => setWhatsAppModalOpen(false)}
        data={shareData}
      />
    </div>
  );
};
